import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Trash2, Plus, Minus, ShoppingCart, MessageCircle, Package,
  ArrowLeft, Phone, CheckCircle, Loader2, CreditCard
} from "lucide-react";
import { useCart } from "../contexts/CartContext";
import API, { formatError, getImageUrl } from "../api";
import { formatWhatsApp } from "../utils/whatsapp";

const formatPrice = (p) =>
  new Intl.NumberFormat("fr-SN", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(p);

const defaultFlow = () => ({
  step: "idle", // idle | phone_input | payment_info | whatsapp_ready | completed
  clientPhone: "",
  clientName: "",
  currentOrder: null,
  whatsappContact: null,
  loading: false,
  error: "",
});

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, vendorGroups, cartTotal } = useCart();
  const [vendorFlows, setVendorFlows] = useState({});
  const [sessionId] = useState(() => {
    let sid = sessionStorage.getItem("jkl_sid");
    if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("jkl_sid", sid); }
    return sid;
  });

  const getFlow = (vid) => vendorFlows[vid] || defaultFlow();
  const updateFlow = (vid, updates) =>
    setVendorFlows(prev => ({ ...prev, [vid]: { ...(prev[vid] || defaultFlow()), ...updates } }));

  const trackEvent = async (type, extra = {}) => {
    try { await API.post("/api/events", { type, session_id: sessionId, ...extra }); } catch (_) {}
  };

  useEffect(() => {
    if (cart.length > 0) trackEvent("cart_view", { cart_count: cart.length });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInitiateOrder = async (vendorId, group) => {
    const flow = getFlow(vendorId);
    if (!flow.clientPhone.trim()) return;
    updateFlow(vendorId, { loading: true, error: "" });
    try {
      const { data } = await API.post("/api/orders/anonymous-contact", {
        vendor_id: vendorId,
        items: group.items.map(i => ({
          product_id: i.product_id, name: i.name,
          price: i.price, quantity: i.quantity, image: i.image || null,
        })),
        total_amount: group.subtotal,
        client_phone: flow.clientPhone.trim(),
        client_name: flow.clientName.trim() || undefined,
        session_id: sessionId,
      });
      updateFlow(vendorId, { currentOrder: data, step: "payment_info", loading: false });
      trackEvent("order_initiated", { vendor_id: vendorId, order_id: data._id, client_phone: flow.clientPhone.trim() });
    } catch (e) {
      updateFlow(vendorId, { error: formatError(e), loading: false });
    }
  };

  const handleDeclarePayment = async (vendorId) => {
    const flow = getFlow(vendorId);
    if (!flow.currentOrder?._id) return;
    updateFlow(vendorId, { loading: true, error: "" });
    try {
      const { data } = await API.put(`/api/orders/${flow.currentOrder._id}/declare-payment`);
      updateFlow(vendorId, { whatsappContact: data.vendor_contact, step: "whatsapp_ready", loading: false });
      trackEvent("payment_declared", { vendor_id: vendorId, order_id: flow.currentOrder._id });
      trackEvent("whatsapp_unlocked", { vendor_id: vendorId, order_id: flow.currentOrder._id });
    } catch (e) {
      updateFlow(vendorId, { error: formatError(e), loading: false });
    }
  };

  const handleComplete = async (vendorId, group) => {
    const flow = getFlow(vendorId);
    if (!flow.currentOrder?._id) return;
    updateFlow(vendorId, { loading: true, error: "" });
    try {
      await API.put(`/api/orders/${flow.currentOrder._id}/complete`);
      updateFlow(vendorId, { step: "completed", loading: false });
      // Remove cart items for this vendor now that order is confirmed
      group.items.forEach(i => removeFromCart(i.product_id));
      trackEvent("order_completed", { vendor_id: vendorId, order_id: flow.currentOrder._id });
    } catch (e) {
      updateFlow(vendorId, { error: formatError(e), loading: false });
    }
  };

  if (cart.length === 0) {
    const hasActive = Object.values(vendorFlows).some(f =>
      ["phone_input", "payment_info", "whatsapp_ready"].includes(f.step)
    );
    if (!hasActive) {
      return (
        <div className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-4 pb-24 md:pb-0">
          <ShoppingCart size={64} className="text-gray-300 mb-4" />
          <h2 className="text-xl font-extrabold text-[#232F3E] mb-2">Votre panier est vide</h2>
          <p className="text-gray-500 text-sm mb-6">Découvrez nos produits et ajoutez-en à votre panier</p>
          <Link to="/catalogue" className="bg-[#FF9900] text-[#111827] font-bold px-6 py-3 rounded-md hover:bg-[#E68A00] transition-colors" data-testid="browse-products-btn">
            Explorer les produits
          </Link>
        </div>
      );
    }
  }

  // Merge vendor groups: active cart groups + flows that have moved past idle
  const allVendorIds = new Set([
    ...Object.keys(vendorGroups),
    ...Object.keys(vendorFlows).filter(vid => vendorFlows[vid]?.step !== "idle"),
  ]);

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-3xl mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-5">
          <Link to="/catalogue" className="p-1.5 hover:bg-gray-100 rounded" data-testid="back-to-catalog">
            <ArrowLeft size={18} className="text-gray-600" />
          </Link>
          <h1 className="text-xl font-extrabold text-[#232F3E]">
            Mon panier {cart.length > 0 && `(${cart.length} article${cart.length > 1 ? "s" : ""})`}
          </h1>
        </div>

        {/* Articles */}
        {cart.length > 0 && (
          <div className="space-y-3 mb-5">
            {cart.map((item) => (
              <div key={item.product_id} className="flex gap-3 bg-white border border-gray-200 rounded-md p-3" data-testid="cart-item">
                <div className="w-16 h-16 rounded bg-gray-100 shrink-0 overflow-hidden">
                  {item.image ? (
                    <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://via.placeholder.com/64"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Package size={24} className="text-gray-300" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-[#232F3E] line-clamp-2">{item.name}</p>
                  <p className="text-sm font-black text-[#232F3E] mt-0.5">{formatPrice(item.price)}</p>
                  <p className="text-xs text-gray-400">{item.vendor_shop_name}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button onClick={() => removeFromCart(item.product_id)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid="remove-cart-item">
                    <Trash2 size={15} />
                  </button>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center hover:bg-gray-200"><Minus size={12} /></button>
                    <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center hover:bg-gray-200"><Plus size={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Checkout WhatsApp-first — par vendeur */}
        {[...allVendorIds].map((vendorId) => {
          const group = vendorGroups[vendorId];
          const flow = getFlow(vendorId);

          if (flow.step === "completed") {
            return (
              <div key={vendorId} className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 text-center" data-testid={`vendor-completed-${vendorId}`}>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <p className="font-bold text-green-700 text-sm">Commande confirmée !</p>
                <p className="text-xs text-gray-500 mt-1">Merci pour votre achat.</p>
              </div>
            );
          }

          if (!group) return null; // Group cleared but flow is past whatsapp_ready — handled above

          return (
            <div key={vendorId} className="bg-white border border-gray-200 rounded-xl p-4 mb-4" data-testid={`vendor-checkout-${vendorId}`}>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                <p className="font-bold text-[#232F3E] text-sm">{group.vendor_shop_name || "Vendeur"}</p>
                <p className="font-black text-[#232F3E]">{formatPrice(group.subtotal)}</p>
              </div>

              {/* STEP: idle */}
              {flow.step === "idle" && (
                <button
                  onClick={() => { trackEvent("click_buy", { vendor_id: vendorId }); updateFlow(vendorId, { step: "phone_input", error: "" }); }}
                  className="w-full bg-[#25D366] text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 hover:bg-[#20BD5A] transition-colors"
                  data-testid={`cart-commander-btn-${vendorId}`}
                >
                  <MessageCircle size={16} /> Commander via WhatsApp
                </button>
              )}

              {/* STEP: phone_input */}
              {flow.step === "phone_input" && (
                <div className="space-y-3" data-testid={`phone-input-step-${vendorId}`}>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateFlow(vendorId, { step: "idle", error: "" })} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <ArrowLeft size={14} />
                    </button>
                    <p className="text-sm font-bold text-[#232F3E]">Vos coordonnées</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Numéro de téléphone *</label>
                    <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:border-[#25D366] transition-colors">
                      <Phone size={14} className="text-gray-400 shrink-0" />
                      <input
                        type="tel"
                        placeholder="ex: 77 123 45 67"
                        value={flow.clientPhone}
                        onChange={(e) => updateFlow(vendorId, { clientPhone: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && handleInitiateOrder(vendorId, group)}
                        className="flex-1 text-sm outline-none bg-transparent"
                        data-testid={`cart-phone-input-${vendorId}`}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      Prénom <span className="text-gray-400 font-normal">(optionnel)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="ex: Aminata"
                      value={flow.clientName}
                      onChange={(e) => updateFlow(vendorId, { clientName: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && handleInitiateOrder(vendorId, group)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#25D366] transition-colors"
                      data-testid={`cart-name-input-${vendorId}`}
                    />
                  </div>
                  {flow.error && <p className="text-red-500 text-xs" data-testid="cart-order-error">{flow.error}</p>}
                  <button
                    onClick={() => handleInitiateOrder(vendorId, group)}
                    disabled={!flow.clientPhone.trim() || flow.loading}
                    className="w-full bg-[#25D366] text-white font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#20BD5A] transition-colors"
                    data-testid={`cart-initiate-btn-${vendorId}`}
                  >
                    {flow.loading ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                    {flow.loading ? "Traitement..." : "Continuer"}
                  </button>
                </div>
              )}

              {/* STEP: payment_info */}
              {flow.step === "payment_info" && (
                <div className="space-y-3" data-testid={`payment-info-step-${vendorId}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <CreditCard size={13} className="text-blue-600" />
                    </div>
                    <p className="text-sm font-bold text-[#232F3E]">Informations de paiement</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Envoyez <span className="font-bold text-[#232F3E]">{formatPrice(group.subtotal)}</span> au vendeur via :
                  </p>

                  {flow.currentOrder?.vendor_payment_info?.phone_wave && (
                    <div className="flex items-center gap-3 bg-[#E8F7FF] rounded-lg px-3 py-2.5" data-testid="cart-wave-info">
                      <div className="w-7 h-7 rounded-full bg-[#1CBBEE] flex items-center justify-center shrink-0">
                        <span className="text-white text-[10px] font-black">W</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-500 font-medium">Wave</p>
                        <p className="text-sm font-black text-[#232F3E]">{flow.currentOrder.vendor_payment_info.phone_wave}</p>
                      </div>
                      <button
                        onClick={() => navigator.clipboard?.writeText(flow.currentOrder.vendor_payment_info.phone_wave)}
                        className="text-xs text-[#1CBBEE] font-semibold shrink-0"
                      >Copier</button>
                    </div>
                  )}

                  {flow.currentOrder?.vendor_payment_info?.phone_orange && (
                    <div className="flex items-center gap-3 bg-[#FFF3E0] rounded-lg px-3 py-2.5" data-testid="cart-orange-info">
                      <div className="w-7 h-7 rounded-full bg-[#FF6600] flex items-center justify-center shrink-0">
                        <span className="text-white text-[10px] font-black">O</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-500 font-medium">Orange Money</p>
                        <p className="text-sm font-black text-[#232F3E]">{flow.currentOrder.vendor_payment_info.phone_orange}</p>
                      </div>
                      <button
                        onClick={() => navigator.clipboard?.writeText(flow.currentOrder.vendor_payment_info.phone_orange)}
                        className="text-xs text-[#FF6600] font-semibold shrink-0"
                      >Copier</button>
                    </div>
                  )}

                  {!flow.currentOrder?.vendor_payment_info?.phone_wave && !flow.currentOrder?.vendor_payment_info?.phone_orange && (
                    <p className="text-xs text-gray-400 text-center py-1">
                      Le vendeur vous communiquera ses coordonnées de paiement sur WhatsApp.
                    </p>
                  )}

                  {flow.error && <p className="text-red-500 text-xs" data-testid="cart-order-error">{flow.error}</p>}
                  <button
                    onClick={() => handleDeclarePayment(vendorId)}
                    disabled={flow.loading}
                    className="w-full bg-[#232F3E] text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#37475A] transition-colors"
                    data-testid={`cart-declare-btn-${vendorId}`}
                  >
                    {flow.loading ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                    {flow.loading ? "Vérification..." : "J'ai effectué le paiement"}
                  </button>
                </div>
              )}

              {/* STEP: whatsapp_ready */}
              {flow.step === "whatsapp_ready" && (
                <div className="space-y-3" data-testid={`whatsapp-ready-step-${vendorId}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <CheckCircle size={13} className="text-green-600" />
                    </div>
                    <p className="text-sm font-bold text-green-700">Paiement déclaré !</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Contactez le vendeur sur WhatsApp pour la livraison :
                  </p>
                  {flow.whatsappContact?.whatsapp_number && (
                    <a
                      href={`https://wa.me/${formatWhatsApp(flow.whatsappContact.whatsapp_number)}?text=${encodeURIComponent(`Bonjour ${flow.whatsappContact.shop_name || "vendeur"}, j'ai effectué mon paiement de ${formatPrice(group.subtotal)} pour ma commande sur Jokkoleen. Réf: #${flow.currentOrder?._id?.slice(-6).toUpperCase()}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("whatsapp_opened", { vendor_id: vendorId, order_id: flow.currentOrder?._id })}
                      className="w-full bg-[#25D366] text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-[#20BD5A] transition-colors"
                      data-testid={`cart-whatsapp-btn-${vendorId}`}
                    >
                      <MessageCircle size={15} /> Continuer sur WhatsApp
                    </a>
                  )}
                  <button
                    onClick={() => handleComplete(vendorId, group)}
                    disabled={flow.loading}
                    className="w-full bg-white border border-green-300 text-green-700 font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-green-50 transition-colors disabled:opacity-50"
                    data-testid={`cart-complete-btn-${vendorId}`}
                  >
                    {flow.loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    {flow.loading ? "Traitement..." : "Produit reçu"}
                  </button>
                  {flow.error && <p className="text-red-500 text-xs" data-testid="cart-order-error">{flow.error}</p>}
                </div>
              )}
            </div>
          );
        })}

        {/* Total */}
        {cart.length > 0 && (
          <div className="bg-[#232F3E] text-white rounded-md p-4 flex items-center justify-between">
            <span className="font-semibold">Total panier</span>
            <span className="text-xl font-black text-[#FF9900]">{formatPrice(cartTotal)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
